import { PropsWithChildren, useState } from 'react';
import { ObjectKey } from 'utils/tsHelpers';
import { ErrorBoundary } from 'modules/error/ErrorBoundary';
import { Product, Team } from 'teams';
import { MissionControlModule } from 'modules/missionControl/constants';
import { useGetLaunchpad } from '../hooks/useGetLaunchpad';
import { LaunchpadContext } from './LaunchpadContext';
import { LaunchpadModule } from '../Launchpad';
import { HomepageErrorBoundaryState } from '../../HomepageErrorBoundaryState';

export const LaunchpadProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const [resourceTypes, setResourceTypes] = useState<string[]>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);

  const {
    activityItems = [],
    activityItemsCount,
    isLoading,
    isLoadingMore,
    hasError,
    fetchNextPage,
    allItemsFetched,
  } = useGetLaunchpad(
    {
      resourceTypes,
      actionTypes,
    },
    {
      retry: false,
    },
  );

  const handleClearFilters = () => {
    setResourceTypes([]);
    setActionTypes([]);
  };

  const handleLoadMore = () => {
    if (!allItemsFetched) fetchNextPage();
  };

  return (
    <LaunchpadContext.Provider
      value={{
        actionTypes,
        activityItems,
        activityItemsCount,
        handleClearFilters,
        handleLoadMore,
        handleRetry: handleClearFilters,
        hasError,
        isExpanded,
        isLoading,
        isLoadingMore,
        resourceTypes,
        setActionTypes,
        setIsExpanded,
        setResourceTypes,
      }}
    >
      {children}
    </LaunchpadContext.Provider>
  );
};

export const withLaunchpadProvider =
  <LaunchpadConsumerProps extends Record<ObjectKey, unknown>>(
    LaunchpadConsumer: React.FC<LaunchpadConsumerProps>,
  ) =>
  (props: LaunchpadConsumerProps) => (
    <ErrorBoundary
      teamName={Team.NOVA_TEAM}
      productName={Product.HOMEPAGE}
      errorContext={{
        module: MissionControlModule.USER_ACTIVITY,
      }}
      errorMarkup={
        <LaunchpadModule>
          <HomepageErrorBoundaryState data-test="launchpad-content-error-boundary-state" />
        </LaunchpadModule>
      }
    >
      <LaunchpadProvider>
        <LaunchpadConsumer {...props} />
      </LaunchpadProvider>
    </ErrorBoundary>
  );
